import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { DeleteFileConfirmationPopupComponent } from '../delete-file-confirmation-popup/delete-file-confirmation-popup.component';
import {
  FILE_UPLOAD_LABELS,
  FileData,
  FileParseType,
  FileReturn,
  ParsedFileData,
  UploadStatus,
} from './file-upload.constants';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent implements OnInit {
  readonly CONSTANTS = { ...FILE_UPLOAD_LABELS };
  readonly IMAGES = IMAGE_SRC;
  readonly fileUploadStatus = UploadStatus;
  readonly chunkSize = 10;

  fileProgress = 0;
  uploadStatus!: UploadStatus;
  tryAgain = false;
  isTrackingNumberUpload = false;
  acceptedFileTypes: string = '';
  reader = new FileReader();
  returnFile: FileReturn = {
    fileName: '',
    fileSize: 0,
    returnData: [],
  };
  fileErrorMessage = '';
  ediFile!: File;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FileData,
    public readonly dialogRef: MatDialogRef<FileUploadComponent>,
    private readonly dialog: MatDialog,
  ) {
    this.isTrackingNumberUpload = !!(this.data?.trackingNumber ?? false);
  }

  ngOnInit() {
    if (this.data?.sci) {
      this.acceptedFileTypes = 'text/csv, text/plain, application/vnd.ms-excel';
    } else {
      this.acceptedFileTypes =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, text/plain';
    }
  }

  /**
   *Execute when file drag and drop
   *
   * @param {*} $event
   * @memberof FileUploadComponent
   */
  onFileDropped($event: FileList | never[]) {
    if (this.acceptedFileTypes.includes($event[0].type)) {
      if (this.data.sci) {
        this.returnFile.fileName = $event[0].name;
        this.returnFile.fileSize = $event[0].size;
        this.ediFile = $event[0];
        this.uploadStatus = UploadStatus.SUCCESS;
      } else {
        this.returnFile.fileName = $event[0].name;
        this.returnFile.fileSize = $event[0].size;
        this.readFile($event[0]).then((val) => {
          this.parseFile(val, this.data.fileType);
        });
      }
    } else {
      this.uploadStatus = UploadStatus.FAILED;
      this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_FORMAT;
    }
  }

  /**
   * Used to handle File browse
   *
   * @param {*} $event
   * @memberof FileUploadComponent
   */
  fileBrowseHandler($event: Event) {
    const files = ($event.target as HTMLInputElement)?.files;
    if (!files || !files.length) {
      return;
    }
    if (this.acceptedFileTypes.includes(files[0].type)) {
      if (this.data.sci) {
        this.returnFile.fileName = files[0].name;
        this.returnFile.fileSize = files[0].size;
        this.ediFile = files[0];
        this.uploadStatus = UploadStatus.SUCCESS;
      } else {
        this.returnFile.fileName = files[0].name;
        this.returnFile.fileSize = files[0].size;
        this.readFile(files[0]).then((val) => {
          this.parseFile(val, this.data.fileType);
        });
      }
    } else {
      this.uploadStatus = UploadStatus.FAILED;
      this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_FORMAT;
    }
  }

  /**
   *Close file upload popup
   *
   * @memberof FileUploadComponent
   */
  closeFileUploadPopup() {
    if (this.returnFile.fileName.length > 0) {
      this.cancel();
    } else {
      this.dialogRef.close();
    }
  }

  submit() {
    this.dialogRef.close(this.data.sci ? this.ediFile : this.returnFile);
  }

  cancel() {
    this.dialog.open(DeleteFileConfirmationPopupComponent, {
      disableClose: true,
      width: '25rem',
      data: this.returnFile.fileName,
    });
  }

  private async readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this.reader.onload = () => resolve(this.reader.result as string);
      this.reader.onerror = (error) => {
        this.uploadStatus = UploadStatus.FAILED;
        this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE;
        return reject(error);
      };
      this.reader.readAsText(file);
    });
  }

  private parseFile(
    file: string,
    fileType: FileParseType = FileParseType.TRACKINGNUMBER_ONLY,
  ) {
    if (this.data.sci) {
    } else {
      switch (fileType) {
        case FileParseType.TRACKINGNUMBER_ONLY:
          this.parseTrackingNumberFile(file.trim().toUpperCase());
          break;
        case FileParseType.SIZE_AUDIT:
          this.parseSizeAuditFile(file.trim().toUpperCase());
          break;
        case FileParseType.WAREHOUSE_SCAN:
          this.parseWarehouseScanFile(file.trim().toUpperCase());
          break;
        case FileParseType.WEIGHT_AUDIT:
          this.parseWeightFile(file.trim().toUpperCase());
          break;
      }
    }
  }

  private parseTrackingNumberFile(file: string) {
    const returnVals: ParsedFileData[][] = [];
    let vals = file
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    vals?.forEach((item, index) => {
      const splitValues = item.trim().split(',');
      if (splitValues.length !== 1) {
        this.uploadStatus = UploadStatus.FAILED;
        this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_DATA.replace(
          '%a',
          `tracking number`,
        );
        return;
      } else {
        this.uploadStatus = UploadStatus.SUCCESS;
        const chunkIndex = Math.floor(index / this.chunkSize);
        if (!returnVals[chunkIndex]) {
          returnVals[chunkIndex] = [];
        }
        returnVals[chunkIndex].push({ trackingNumber: item });
      }
    });

    this.returnFile.returnData = returnVals;
  }

  private parseSizeAuditFile(file: string) {
    const returnVals: ParsedFileData[][] = [];
    let vals: string[];

    vals = file
      .trim()
      .split('\r\n')
      .flatMap((val) => val);

    vals?.forEach((item, index) => {
      const splitValues = item.trim().split(',');
      if (splitValues.length !== 6) {
        this.uploadStatus = UploadStatus.FAILED;
        this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_DATA.replace(
          '%a',
          `tracking number, length, width, height, unit, uow`,
        );
        return;
      } else {
        const chunkIndex = Math.floor(index / this.chunkSize);
        if (!returnVals[chunkIndex]) {
          returnVals[chunkIndex] = [];
        }

        const data: ParsedFileData = {
          trackingNumber: splitValues[0].trim(),
          length: splitValues[1].trim(),
          width: splitValues[2].trim(),
          height: splitValues[3].trim(),
          unit: splitValues[4].trim(),
          uow: splitValues[5].trim(),
        };
        returnVals[chunkIndex].push(data);
        this.uploadStatus = UploadStatus.SUCCESS;
      }
    });

    this.returnFile.returnData = returnVals;
  }

  private parseWarehouseScanFile(file: string) {
    const returnVals: ParsedFileData[][] = [];
    let vals: string[];

    vals = file
      .trim()
      .split('\r\n')
      .flatMap((val) => val);

    vals?.forEach((item, index) => {
      const splitValues = item.trim().split(',');
      if (splitValues.length !== 2) {
        this.uploadStatus = UploadStatus.FAILED;
        this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_DATA.replace(
          '%a',
          `tracking number, location`,
        );
        return;
      } else {
        const chunkIndex = Math.floor(index / this.chunkSize);
        if (!returnVals[chunkIndex]) {
          returnVals[chunkIndex] = [];
        }
        const data: ParsedFileData = {
          trackingNumber: splitValues[0].trim(),
          location: splitValues[1].trim(),
        };
        returnVals[chunkIndex].push(data);
        this.uploadStatus = UploadStatus.SUCCESS;
      }
    });

    this.returnFile.returnData = returnVals;
  }

  private parseWeightFile(file: string) {
    const returnVals: ParsedFileData[][] = [];
    let vals: string[];

    vals = file
      .trim()
      .split('\r\n')
      .flatMap((val) => val);

    vals?.forEach((item, index) => {
      const splitValues = item.trim().split(',');
      if (splitValues.length !== 4) {
        this.uploadStatus = UploadStatus.FAILED;
        this.fileErrorMessage = FILE_UPLOAD_LABELS.INVALID_FILE_DATA.replace(
          '%a',
          `tracking number, weight, unit, uow`,
        );
        return;
      } else {
        const chunkIndex = Math.floor(index / this.chunkSize);
        if (!returnVals[chunkIndex]) {
          returnVals[chunkIndex] = [];
        }

        const data: ParsedFileData = {
          trackingNumber: splitValues[0].trim(),
          weight: splitValues[1].trim(),
          unit: splitValues[2].trim(),
          uow: splitValues[3].trim(),
        };
        returnVals[chunkIndex].push(data);
        this.uploadStatus = UploadStatus.SUCCESS;
      }
    });

    this.returnFile.returnData = returnVals;
  }
}
